'use client'

import { Logo } from '@/components/logo'
import { Card } from '@/components/ui/card'
import { Loading } from '@/components/ui/loading'
import { usePageTitle } from '@/hooks/use-page-title'

import { ReactNode, Suspense, ViewTransition } from 'react'

interface AuthShellProps {
  children: ReactNode
  showFooter?: boolean
  footerText?: string
}

/**
 * Shared visual shell for the "plain" (non-app) pages: sign-in, password
 * reset, invitation acceptance, account creation, org selection, etc. A
 * centered card over a blue radial glow that pours from the top and fades into
 * the background. Theme-aware via the --primary / --background tokens.
 */
export function AuthShell({
  children,
  showFooter = true,
  footerText = 'Need help? Contact your administrator'
}: AuthShellProps) {
  usePageTitle()

  return (
    <div className="bg-background relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Blue radial glow from the top, fading into the background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 90% 55% at 50% -10%, color-mix(in oklch, var(--primary) 22%, transparent), transparent 65%)'
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <Card className="bg-card border p-6 shadow-2xl sm:p-8">
          <div className="mb-6 flex items-center justify-center">
            <Logo size={220} />
          </div>

          {/* ViewTransition cross-fades the form between auth navigations. */}
          <Suspense
            fallback={
              <div className="flex justify-center py-12">
                <Loading message="Loading..." size="lg" />
              </div>
            }
          >
            <ViewTransition>{children}</ViewTransition>
          </Suspense>

          {showFooter && (
            <p className="text-muted-foreground mt-6 text-center text-xs">
              {footerText}
            </p>
          )}
        </Card>
      </div>
    </div>
  )
}
